import { Link } from 'react-router';
import { formatPickRecord, formatTies } from '../../utils/predictionScorecard';
import ProbabilityPanel from './ProbabilityPanel';

const STATUS_LABEL = { final: 'Final', 'final-late': 'Final · late edition', pending: 'Pending' };

function statusLabel(row) {
  return row.status === 'pending' && row.late ? 'Pending · late edition' : STATUS_LABEL[row.status];
}

function Pips({ correct, evaluated, ties }) {
  const pips = [
    ...Array.from({ length: correct }, () => 'hit'),
    ...Array.from({ length: Math.max(evaluated - correct, 0) }, () => 'miss'),
    ...Array.from({ length: ties }, () => 'tie'),
  ];
  return <span className="forecast-season__pips" aria-hidden="true">{pips.map((kind, index) => <i key={index} className={`is-${kind}`} />)}</span>;
}

function NotGraded() {
  return <><span className="forecast-season__dash" aria-hidden="true">—</span><span className="forecast-sr">Not graded yet</span></>;
}

function scrollToTop() {
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
}

export default function SeasonScorecard({ scorecard, selectedIssue, probability = null }) {
  if (!scorecard.rows.length) return null;
  const { rows, totals } = scorecard;
  const season = selectedIssue?.season ?? rows[0].season;
  const hasLate = rows.some((row) => row.late);
  return <section className="forecast-season" id="forecast-season" aria-labelledby="forecast-season-title">
    <div className="forecast-shell">
      <div className="forecast-section-heading">
        <div><p className="forecast-kicker">{season} season scorecard</p><h2 id="forecast-season-title">Every week, graded.</h2></div>
        <p>A pick is correct when that team finishes with more points. Team MAE is the average miss on each team score, in points.</p>
      </div>
      <table className="forecast-season__table">
        <caption className="forecast-sr">AI Picks {season} scorecard by week</caption>
        <thead><tr><th scope="col">Week</th><th scope="col">Picks correct</th><th scope="col">Team <abbr title="Mean absolute error">MAE</abbr></th><th scope="col">Status</th></tr></thead>
        <tbody>{rows.map((row) => {
          const current = selectedIssue && row.season === selectedIssue.season && row.week === selectedIssue.week;
          const graded = row.status !== 'pending';
          return <tr key={`${row.season}-${row.week}`} className={`forecast-season__row is-${row.status}${current ? ' is-current' : ''}`}>
            <th scope="row"><Link to={`/ai-picks/${row.season}/week-${row.week}`} aria-current={current ? 'page' : undefined} onClick={scrollToTop}>Week {row.week}</Link></th>
            <td className="forecast-season__picks">{graded ? <><b>{formatPickRecord(row)}</b><Pips correct={row.correctPicks} evaluated={row.evaluatedPicks} ties={row.tiedMatchups} />{row.tiedMatchups > 0 && <small>{formatTies(row.tiedMatchups)}</small>}</> : <NotGraded />}</td>
            <td className="forecast-season__mae">{graded ? row.meanAbsoluteError.toFixed(1) : <NotGraded />}</td>
            <td><span className={`forecast-season__status is-${row.status}${row.late ? ' is-late' : ''}`}>{statusLabel(row)}</span></td>
          </tr>;
        })}</tbody>
        <tfoot><tr className="forecast-season__total">
          <th scope="row">Season</th>
          <td className="forecast-season__picks">{totals.gradedWeeks ? <><b>{formatPickRecord(totals)}</b>{totals.tiedMatchups > 0 && <small>{formatTies(totals.tiedMatchups)}</small>}</> : <NotGraded />}</td>
          <td className="forecast-season__mae">{totals.gradedWeeks ? totals.meanAbsoluteError.toFixed(1) : <NotGraded />}</td>
          <td><span className="forecast-season__status">{totals.gradedWeeks} of {rows.length} graded</span></td>
        </tr></tfoot>
      </table>
      {hasLate && <p className="forecast-season__note">Late edition: captured after the first NFL kickoff. It counts here and is left out of the pregame-only record.</p>}
      <ProbabilityPanel probability={probability} />
    </div>
  </section>;
}
